import { FundingApplicationSchemas } from "@wayflyer/api-types";
import { FormattedMessage, type IntlShape } from "react-intl";

import { getConnectorNameForFE } from "./common";

export const getReconnectConnectionDetailsText = (airData: FundingApplicationSchemas.ReconnectConnectionAIRSchema) => {
  switch (airData.connection_meta_data?.type) {
    case "BANKING": {
      const metaData = airData.connection_meta_data as FundingApplicationSchemas.ReconnectBankConnectionMetaData;
      const bankName = airData.connection_identifier ?? "";

      if (metaData.account_masks.length === 0) {
        return (
          <FormattedMessage
            id="shared-ui: air reconnect connection details text banking (no mask)"
            defaultMessage="<p>Please reconnect your {bankName} bank.</p>"
            description="Reconnect connection details text for banking with no mask. {bankName} is the bank name (may not be available)."
            values={{
              p: (chunks: any) => chunks,
              bankName,
            }}
          />
        );
      }
      if (metaData.account_masks.length === 1) {
        return (
          <FormattedMessage
            id="shared-ui: air reconnect connection details text banking (singular mask)"
            defaultMessage="<p>Please reconnect your {bankName} bank with account ending in {singularMask}.</p>"
            description="Reconnect connection details text for banking with singular mask. {bankName} is the bank name (may not be available), {singularMask} is the last 4 digits of the account number."
            values={{
              p: (chunks: any) => chunks,
              bankName,
              singularMask: metaData.account_masks[0] ?? "",
            }}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="shared-ui: air reconnect connection details text banking (multiple masks)"
            defaultMessage="<p>Please reconnect your {bankName} bank with accounts ending in {maskList} and {maskListFinal}.</p>"
            description="Reconnect connection details text for banking with multiple masks. {bankName} is the bank name (may not be available), {maskList} is a comma separated list of the last 4 digits of the account numbers, and {maskListFinal} is the last 4 digits of the last account number."
            values={{
              p: (chunks: any) => chunks,
              bankName,
              maskList: metaData.account_masks.slice(0, -1).join(", "), // all but last
              maskListFinal: metaData.account_masks[metaData.account_masks.length - 1], // last
            }}
          />
        );
      }
    }
    default: {
      return airData.connection_identifier ? (
        <FormattedMessage
          id="shared-ui: air reconnect connection details text platform"
          defaultMessage="<p>Please reconnect your {platform} account: {identifier}</p>"
          values={{
            p: (chunks: any) => chunks,
            platform: getConnectorNameForFE(airData.connection_type),
            identifier: airData.connection_identifier,
          }}
        />
      ) : (
        <FormattedMessage
          id="shared-ui: air reconnect connection details text platform (no identifier)"
          defaultMessage="<p>Please reconnect your {platform} account.</p>"
          values={{
            p: (chunks: any) => chunks,
            platform: getConnectorNameForFE(airData.connection_type),
          }}
        />
      );
    }
  }
};

export const getBankAccountMasksAsString = (intl: IntlShape, accountMasks: string[]) => {
  if (accountMasks.length === 0) {
    return "";
  }
  if (accountMasks.length === 1) {
    return accountMasks[0];
  }
  return intl.formatMessage(
    {
      id: "shared-ui: air reconnect connection multiple account masks",
      defaultMessage: "{maskList} and {maskListFinal}",
    },
    {
      maskList: accountMasks.slice(0, -1).join(", "), // all but last
      maskListFinal: accountMasks[accountMasks.length - 1], // last
    }
  );
};
